import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import logoImage from '../assets/logo.png'; // Import logo dari assets

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      toast.error('Nama pengguna dan kata sandi harus diisi');
      return;
    }

    setLoading(true);
    try {
      const result = await login(username, password);
      if (result.success) {
        toast.success(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">✅ Login Berhasil!</div>
            <div className="text-sm">Selamat datang, {username}</div>
          </div>,
          { duration: 3000 }
        );
        navigate('/dashboard');
      } else {
        // Login gagal
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">❌ Login Gagal</div>
            <div className="text-sm">{result.message || 'Username atau password salah'}</div>
          </div>,
          { duration: 5000 }
        );
      }
    } catch (error) {
      toast.error(
        <div className="flex flex-col gap-1">
          <div className="font-semibold">❌ Terjadi Kesalahan</div>
          <div className="text-sm">Tidak dapat terhubung ke server</div>
        </div>,
        { duration: 5000 }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="space-y-3 text-center pb-4">
            <Logo
              src={branding.logo_url || logoImage}
              alt="Logo SSL Monitor"
              size="2xl"
              className="mx-auto"
            />
            <div>
              <CardTitle>{branding.app_title}</CardTitle>
              <CardDescription className="mt-1">
                {branding.app_subtitle}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="username">Nama Pengguna</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Masukkan nama pengguna"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  autoComplete="username"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Kata Sandi</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Masukkan kata sandi"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Memuat...
                  </span>
                ) : (
                  'Masuk'
                )}
              </Button>
            </form>

            <div className="mt-5 text-center text-xs text-muted-foreground space-y-0.5">
              <p>{branding.org_name}</p>
              <p className="font-medium">Pusat Data dan Teknologi Informasi</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        expand={true}
        richColors
        closeButton
        toastOptions={{
          className: 'text-xs',
          style: {
            zIndex: 9999,
          }
        }}
      />
    </div>
  );
}